import React, { FormEvent, useState } from 'react';
import {
  // eslint-disable-next-line no-restricted-imports
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Divider,
  FormControl,
  FormLabel,
  Select,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { DatePicker } from 'src/components/atoms/DatePicker';
import { FormTextInput } from 'src/components/molecules/formInput/FormTextInput';
import { validateEmail } from 'src/utils/email';
import type {
  RefreshFrequency,
  SubscriptionTariff,
} from 'src/api/adminInvites';

// Display labels for tariffs — DB stores slugs, UI shows human-friendly text.
const TARIFF_LABELS: Record<SubscriptionTariff, string> = {
  digital_baseline: 'DIGITAL BASELINE',
  digital_advanced: 'DIGITAL ADVANCED',
  cross_channel_growth: 'CROSS-CHANNEL GROWTH',
  full_scale_enterprise: 'FULL-SCALE ENTERPRISE',
};

const FREQUENCY_LABELS: Record<RefreshFrequency, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
};

// Default `subscriptionUntilDue` — today.
const defaultUntilDue = (): Date => new Date();

const dateToIsoDate = (d: Date): string => {
  // YYYY-MM-DD in local calendar terms (not UTC-shifted, which would move
  // the day for early-morning or late-evening pickers depending on tz).
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export type InviteCompanyAdminSubmit = {
  email: string;
  companyName: string;
  displayName?: string;
  subscriptionRefreshFrequency: RefreshFrequency;
  subscriptionTariff: SubscriptionTariff;
  subscriptionOfflineMediaAvailable: boolean;
  subscriptionModelsAvailable: number;
  subscriptionUntilDue: string;
};

type InviteCompanyAdminModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InviteCompanyAdminSubmit) => void;
  isLoading: boolean;
  errorMessage?: string | null;
};

export const InviteCompanyAdminModal: React.FC<
  InviteCompanyAdminModalProps
> = ({ isOpen, onClose, onSubmit, isLoading, errorMessage }) => {
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [refreshFrequency, setRefreshFrequency] =
    useState<RefreshFrequency>('monthly');
  const [tariff, setTariff] = useState<SubscriptionTariff>('digital_baseline');
  const [offlineMediaAvailable, setOfflineMediaAvailable] = useState(false);
  const [modelsAvailable, setModelsAvailable] = useState(3);
  const [untilDue, setUntilDue] = useState<Date>(defaultUntilDue);
  const [localError, setLocalError] = useState<string | null>(null);

  const isValid =
    email.trim().length > 0 &&
    validateEmail(email.trim()) &&
    companyName.trim().length > 0 &&
    modelsAvailable >= 1 &&
    modelsAvailable <= 999;

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    setLocalError(null);

    if (!email.trim()) {
      setLocalError('Email is required');
      return;
    }
    if (!validateEmail(email.trim())) {
      setLocalError('Invalid email address');
      return;
    }
    if (!companyName.trim()) {
      setLocalError('Company name is required');
      return;
    }
    if (
      !Number.isInteger(modelsAvailable) ||
      modelsAvailable < 1 ||
      modelsAvailable > 999
    ) {
      setLocalError(
        'Models available must be a whole number between 1 and 999',
      );
      return;
    }

    onSubmit({
      email: email.trim(),
      companyName: companyName.trim(),
      displayName: displayName.trim() || undefined,
      subscriptionRefreshFrequency: refreshFrequency,
      subscriptionTariff: tariff,
      subscriptionOfflineMediaAvailable: offlineMediaAvailable,
      subscriptionModelsAvailable: modelsAvailable,
      subscriptionUntilDue: dateToIsoDate(untilDue),
    });
  };

  const handleClose = () => {
    setEmail('');
    setCompanyName('');
    setDisplayName('');
    setRefreshFrequency('monthly');
    setTariff('digital_baseline');
    setOfflineMediaAvailable(false);
    setModelsAvailable(3);
    setUntilDue(defaultUntilDue());
    setLocalError(null);
    onClose();
  };

  const sectionLabel = (label: string) => (
    <Text
      fontFamily="marketingPage"
      fontWeight="semibold"
      fontSize="xs"
      textTransform="uppercase"
      color="content.muted"
      letterSpacing="wider">
      {label}
    </Text>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="16px" bg="surface.panel">
        <ModalCloseButton />
        <ModalHeader pb={2} pt={6}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="xl"
            color="content.title">
            Invite a new customer admin
          </Text>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody px={6} py={4}>
            <VStack align="stretch" spacing={5}>
              {sectionLabel('Contact')}
              <FormTextInput
                useForm={false}
                type="email"
                label="Email"
                placeholder="admin@company.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <FormTextInput
                useForm={false}
                label="Company name"
                placeholder="Acme Corp"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />

              <FormTextInput
                useForm={false}
                label="Display name (optional)"
                placeholder="John Doe"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />

              <Divider />
              {sectionLabel('Subscription')}

              <FormControl isRequired>
                <FormLabel
                  fontFamily="marketingPage"
                  fontWeight={500}
                  fontSize="16px"
                  color="content.title"
                  mb={3}>
                  Tariff
                </FormLabel>
                <Select
                  value={tariff}
                  onChange={(e) =>
                    setTariff(e.target.value as SubscriptionTariff)
                  }
                  fontFamily="marketingPage"
                  bg="surface.panel"
                  borderColor="surface.border"
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}>
                  {(Object.keys(TARIFF_LABELS) as SubscriptionTariff[]).map(
                    (slug) => (
                      <option key={slug} value={slug}>
                        {TARIFF_LABELS[slug]}
                      </option>
                    ),
                  )}
                </Select>
              </FormControl>

              <HStack align="flex-start" spacing={4}>
                <FormControl isRequired>
                  <FormLabel
                    fontFamily="marketingPage"
                    fontWeight={500}
                    fontSize="16px"
                    color="content.title"
                    mb={3}>
                    Refresh frequency
                  </FormLabel>
                  <Select
                    value={refreshFrequency}
                    onChange={(e) =>
                      setRefreshFrequency(e.target.value as RefreshFrequency)
                    }
                    fontFamily="marketingPage"
                    bg="surface.panel"
                    borderColor="surface.border"
                    _focus={{
                      borderColor: 'icon.green',
                      boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                    }}>
                    {(Object.keys(FREQUENCY_LABELS) as RefreshFrequency[]).map(
                      (slug) => (
                        <option key={slug} value={slug}>
                          {FREQUENCY_LABELS[slug]}
                        </option>
                      ),
                    )}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel
                    fontFamily="marketingPage"
                    fontWeight={500}
                    fontSize="16px"
                    color="content.title"
                    mb={3}>
                    Models available
                  </FormLabel>
                  <NumberInput
                    value={modelsAvailable}
                    onChange={(_, valueAsNumber) => {
                      if (Number.isFinite(valueAsNumber)) {
                        setModelsAvailable(valueAsNumber);
                      }
                    }}
                    min={1}
                    max={999}
                    step={1}
                    fontFamily="marketingPage">
                    <NumberInputField
                      bg="surface.panel"
                      borderColor="surface.border"
                      _focus={{
                        borderColor: 'icon.green',
                        boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                      }}
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </HStack>

              <Checkbox
                isChecked={offlineMediaAvailable}
                onChange={(e) => setOfflineMediaAvailable(e.target.checked)}
                colorScheme="green">
                <Text fontFamily="marketingPage" fontSize="sm">
                  Offline media channels available
                </Text>
              </Checkbox>

              <FormControl isRequired>
                <FormLabel
                  fontFamily="marketingPage"
                  fontWeight={500}
                  fontSize="16px"
                  color="content.title"
                  mb={3}>
                  Subscription until (inclusive)
                </FormLabel>
                <DatePicker
                  selectedDate={untilDue}
                  onChange={(d) => setUntilDue(d)}
                  minDate={new Date()}
                />
              </FormControl>

              {(localError || errorMessage) && (
                <Text color="red.500" fontSize="sm">
                  {localError || errorMessage}
                </Text>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter gap={3} px={6} pb={6}>
            <Button
              type="button"
              bg="surface.sidebarMuted"
              color="content.body"
              border="1px solid"
              borderColor="surface.border"
              size="md"
              px={6}
              py={2}
              borderRadius="md"
              fontWeight="600"
              buttonName="cancel-admin-invite"
              fontFamily="marketingPage"
              onClick={handleClose}
              flex={1}
              _hover={{ bg: 'sidebar.itemHoverBg' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              bg="icon.green"
              color="white"
              size="md"
              px={6}
              py={2}
              borderRadius="md"
              fontWeight="600"
              buttonName="send-admin-invite"
              fontFamily="marketingPage"
              isLoading={isLoading}
              isDisabled={!isValid}
              flex={1}
              _hover={{ bg: 'green.600' }}>
              Send invite
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
